import React, { useState } from 'react';
import * as gamesApi from '../../api/games';
import './gameDetails.css';

/**
 * Manager-only control for Beat The Gaffer on a game that already exists.
 *
 * Three states worth knowing about, because they are not symmetrical:
 *  - He can only be *added* while the game is still OPEN. After that the button is gone
 *    and the reason is spelled out, rather than letting the manager press it and read a
 *    server error.
 *  - He can be *withdrawn* at any time while he is still standing.
 *  - Once he is knocked out there is nothing left to withdraw, so the button goes and the
 *    panel just calls it: the Big Dog is out.
 *
 * Copy rule, same as everywhere else he appears: he is the Big Dog, never a bot, and how
 * he picks is never explained.
 */
function GafferPanel({ game, onChanged }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const enabled = Boolean(game.gafferEnabled);
  const isOpen = game.gameStatus === 'OPEN';

  // He is flagged on the Player payload, so the roster answers this without a second call.
  // Only meaningful once the game is under way - while it is OPEN nobody has been knocked
  // out yet, and a roster we were not given is not evidence that he is gone.
  const isOut =
    enabled &&
    !isOpen &&
    Array.isArray(game.remainingPlayers) &&
    !game.remainingPlayers.some((player) => player.bot);

  const run = async (action) => {
    setError('');
    setSubmitting(true);
    try {
      await action(game.id);
      await onChanged();
    } catch (err) {
      setError(err.body?.error || 'Could not update the Gaffer');
    } finally {
      setSubmitting(false);
    }
  };

  // Nothing to offer: he is not in, and it is too late to add him
  if (!enabled && !isOpen) {
    return null;
  }

  // Knocked out: nothing to add, nothing to withdraw. Say so and stop offering a button.
  if (isOut) {
    return (
      <div className="gd-panel">
        <div className="gd-panel-main">
          <p className="gd-panel-title">Beat The Gaffer</p>
          <p className="gd-panel-body">The Big Dog is Out. Someone in here saw him off.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gd-panel">
      <div className="gd-panel-main">
        <p className="gd-panel-title">Beat The Gaffer</p>
        <p className="gd-panel-body">
          {enabled
            ? 'The Big Dog is in this game.'
            : 'Are you brave enough to take on the Big Dog in this game?'}
        </p>
        {enabled && (
          <p className="gd-panel-warning">
            If you're playing for a pot, remember he's eligible to win it.
          </p>
        )}
        {!enabled && isOpen && (
          <p className="gd-panel-body">
            He can only join before week 1 locks, so that he starts with the same teams as
            everyone else.
          </p>
        )}
        {error && <p className="gd-panel-error">{error}</p>}
      </div>

      <button
        onClick={() => run(enabled ? gamesApi.removeGaffer : gamesApi.addGaffer)}
        disabled={submitting}
        className={`gd-panel-btn ${enabled ? 'is-remove' : 'is-add'}`}
      >
        {submitting ? 'Saving...' : enabled ? 'Remove the Gaffer' : 'Add the Gaffer'}
      </button>
    </div>
  );
}


export default GafferPanel;
